import React, { useCallback, useEffect, memo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ArrowSquareOutIcon,
  ArrowLineLeftIcon,
  FileTextIcon,
  CaretDownIcon,
  CaretUpIcon,
} from '@phosphor-icons/react';
import { usePopoutWindow } from 'src/hooks/usePopoutWindow';
import RawFilePreview from './RawFilePreview';
import { cn } from '@/lib/utils';

interface RawFilePreviewPopoutProps {
  selectedFiles: File[];
  className?: string;
  selectedSheet?: string; // Optional XLSX sheet selection
  showCard?: boolean; // Whether to show Card wrapper and title header
}

/**
 * Popout-enabled wrapper for RawFilePreview component
 * Based on AIAssistantChatV2's popout implementation
 */
const RawFilePreviewPopout: React.FC<RawFilePreviewPopoutProps> = memo(
  ({ selectedFiles, className = '', selectedSheet, showCard = true }) => {
    const { isPopped, popupWindow, openPopup, closePopup } =
      usePopoutWindow('raw-file-preview');

    // State for collapse/expand functionality when not popped out
    const [isCollapsed, setIsCollapsed] = useState(false);

    const handleOpenPopup = useCallback(() => {
      openPopup('File Preview');
    }, [openPopup]);

    const handleToggleCollapse = useCallback(() => {
      setIsCollapsed((prev) => !prev);
    }, []);

    // Keyboard shortcut: Ctrl/Cmd + P to toggle popout
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.metaKey || event.ctrlKey) && event.key === 'p') {
          event.preventDefault();
          event.stopPropagation();

          if (isPopped) {
            // In popup window: dock back to main window
            closePopup();
          } else {
            // In main window: open popout
            handleOpenPopup();
          }
        }
      };

      // Add listener to main window
      document.addEventListener('keydown', handleKeyDown);

      // Also add listener to popup window if it exists
      if (isPopped && popupWindow) {
        popupWindow.document.addEventListener('keydown', handleKeyDown);
      }

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        if (isPopped && popupWindow) {
          popupWindow.document.removeEventListener('keydown', handleKeyDown);
        }
      };
    }, [isPopped, popupWindow, closePopup, handleOpenPopup]);

    // Don't render anything if no files (after hooks to avoid conditional hook calls)
    if (!selectedFiles.length) {
      return null;
    }

    // Enhanced RawFilePreview with popout controls
    const EnhancedRawFilePreview: React.FC<{
      isInPopup?: boolean;
    }> = ({ isInPopup = false }) => {
      return (
        <div className={cn('relative', !isInPopup && className)}>
          {/* Popout controls overlay */}
          {!isInPopup && (
            <div className="absolute top-4 right-4 z-10 flex gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={handleToggleCollapse}
                    size="icon"
                    variant="outline"
                    className="h-8 w-8 bg-white/90 hover:bg-gray-50 border-gray-300 hover:border-gray-500 shadow-sm"
                    title={isCollapsed ? 'Expand preview' : 'Collapse preview'}
                  >
                    {isCollapsed ? (
                      <CaretDownIcon className="h-4 w-4 text-gray-600" />
                    ) : (
                      <CaretUpIcon className="h-4 w-4 text-gray-600" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isCollapsed ? 'Expand preview' : 'Collapse preview'}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={handleOpenPopup}
                    size="icon"
                    variant="outline"
                    className="h-8 w-8 bg-white/90 hover:bg-blue-50 border-gray-300 hover:border-blue-500 shadow-sm"
                    title="Open in new window"
                  >
                    <ArrowSquareOutIcon className="h-4 w-4 text-gray-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Open file preview in new window (Ctrl/⌘ + P)</p>
                </TooltipContent>
              </Tooltip>
            </div>
          )}

          {/* Popup controls (when in popup window) */}
          {isInPopup && (
            <div className="absolute top-2 right-2 z-50">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={closePopup}
                    size="icon"
                    variant="outline"
                    className="h-8 w-8 bg-white/90 border-gray-300 hover:bg-green-100 hover:border-green-500 shadow-lg"
                    title="Close window and dock back (Ctrl/⌘ + P)"
                  >
                    <ArrowLineLeftIcon className="h-4 w-4 text-gray-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Close window and dock back to main</p>
                </TooltipContent>
              </Tooltip>
            </div>
          )}

          {/* The actual RawFilePreview component */}
          {!isInPopup && isCollapsed ? (
            // Collapsed view - show just a compact summary
            <div
              className="border border-gray-200 rounded-lg p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={handleToggleCollapse}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileTextIcon className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedFiles.length === 1
                        ? selectedFiles[0].name
                        : `${selectedFiles.length} files selected`}
                    </p>
                    <p className="text-xs text-gray-500">
                      {selectedFiles.length === 1
                        ? `${(selectedFiles[0].size / 1024 / 1024).toFixed(
                            2,
                          )} MB`
                        : `${(
                            selectedFiles.reduce((sum, f) => sum + f.size, 0) /
                            1024 /
                            1024
                          ).toFixed(2)} MB total`}
                      {selectedSheet && ` • Sheet: ${selectedSheet}`}
                    </p>
                  </div>
                </div>
                <CaretDownIcon className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          ) : (
            <RawFilePreview
              selectedFiles={selectedFiles}
              className={isInPopup ? 'h-full w-full' : undefined}
              selectedSheet={selectedSheet}
              isInPopup={isInPopup}
              showCard={showCard}
            />
          )}
        </div>
      );
    };

    // When popped out, hide main window version and show only popup
    if (isPopped) {
      const elements = [];

      // Hidden placeholder in main window (maintains layout)
      elements.push(
        <div key="placeholder" className={cn('opacity-30', className)}>
          <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500">
            <FileTextIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">File preview opened in new window</p>
            <p className="text-xs opacity-75">Press Ctrl/⌘ + P to dock back</p>
          </div>
        </div>,
      );

      // Portal to popup window with maximized content
      if (popupWindow) {
        const popupRoot = popupWindow.document.getElementById('popup-root');
        if (popupRoot) {
          elements.push(
            ReactDOM.createPortal(
              <div style={{ height: '100vh', width: '100vw' }}>
                <EnhancedRawFilePreview isInPopup={true} />
              </div>,
              popupRoot,
              'popup-file-preview',
            ),
          );
        }
      }

      return <>{elements}</>;
    }

    // Render in main window only
    return <EnhancedRawFilePreview isInPopup={false} />;
  },
);

RawFilePreviewPopout.displayName = 'RawFilePreviewPopout';

export default RawFilePreviewPopout;
